import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { ToggleGroup } from '@plato/ui/containers/toggle-group';
import { Button } from '@plato/ui/primitives/button';
import { InsetToggleButton } from '@plato/ui/primitives/inset-toggle-button';
import { ToolbarButton } from '@plato/ui/primitives/toolbar-button';
import { Typography } from '@plato/ui/primitives/typography';
import { addDays, formatISO, getWeek, intlFormat, isSameWeek, isToday, parseISO, subDays } from 'date-fns';
import {
    ArrowUpRightIcon,
    CalendarDaysIcon,
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MapIcon,
    PlusIcon,
    UserCircleIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { EventCreateDialogProps } from '../../events/components/event-create-dialog.tsx';
import { EventCreateDialog } from '../../events/components/event-create-dialog.tsx';
import type { ViewMode } from '../../events/components/events-view-mode-selector.tsx';
import { useDayRouteMapsUrl } from '../hooks/use-day-route-maps-url.ts';

export type Props = {
    day: string;
    viewMode: ViewMode;
    className?: string;
    selectedOwnerId: string;
    onChangedDay: (day: string) => void;
    onViewModeChange: (mode: ViewMode) => void;
    onOwnerChange?: (ownerId: string | null) => void;
};

export function EventsOverviewHeaderNew({
    day,
    viewMode,
    selectedOwnerId,
    onChangedDay,
    onViewModeChange,
}: Props): ReactNode {
    const { t } = useTranslation();
    const { userId } = useAppConfig();
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();

    const date = parseISO(day);

    const monthTitle = intlFormat(date, { month: 'long', year: 'numeric' }, { locale });
    const weekLabel = `${t('calendar_week_abbr', { ns: 'events' })}${getWeek(date, { weekStartsOn: 1, firstWeekContainsDate: 4 })}`;

    const step = viewMode === 'week' ? 7 : 1;
    const isCurrentWeek = isSameWeek(date, new Date(), { weekStartsOn: 1 });
    const isCurrentPeriod = viewMode === 'week' ? isCurrentWeek : isToday(date);

    const handleChange = (newDay: Date, direction: 'previous' | 'next' | 'today'): void => {
        sendEvent('event_calendar_period_changed', { direction, view_mode: viewMode });
        onChangedDay(formatISO(newDay, { representation: 'date' }));
    };
    const goToToday = (): void => {
        handleChange(new Date(), 'today');
    };

    const canCreateEvent = selectedOwnerId === userId;
    const openCreateEvent = (): void => {
        void DialogStateManager.show(EventCreateDialog, {
            date: day,
            analyticsSource: 'calendar',
        } satisfies EventCreateDialogProps);
    };

    const { mapsUrl, onOpen } = useDayRouteMapsUrl({
        day,
        ownerId: selectedOwnerId,
        enabled: viewMode === 'day',
    });
    const mapsLinkProps = mapsUrl
        ? { href: mapsUrl, target: '_blank', rel: 'noopener noreferrer', onClick: onOpen }
        : { disabled: true };

    return (
        <div className="@container pt-page">
            <div className="flex flex-wrap items-center gap-cozy">
                {/* Desktop title: month and KW side by side */}
                <div className="hidden shrink-0 items-center gap-cozy @[40rem]:flex">
                    <CalendarIcon className="size-4" />
                    <Typography variant="heading-1">{monthTitle}</Typography>
                    <Typography variant="heading-3" className="font-normal text-content-weaker">
                        {weekLabel}
                    </Typography>
                </div>
                {/* Mobile title: KW as a caption under the month, back-to-today beside it while in another week */}
                <div className="flex min-w-0 flex-1 items-center gap-cozy @[40rem]:hidden">
                    <div className="flex min-w-0 flex-col">
                        <Typography variant="heading-1" className="truncate">
                            {monthTitle}
                        </Typography>
                        <Typography variant="body" className="text-content-weaker">
                            {weekLabel}
                        </Typography>
                    </div>
                    {!isCurrentWeek && (
                        <ToolbarButton
                            aria-label={t('today', { ns: 'events' })}
                            className="size-9 px-0"
                            onClick={goToToday}
                        >
                            {t('today_day_number', {
                                ns: 'events',
                                count: new Date().getDate(),
                                ordinal: true,
                            })}
                        </ToolbarButton>
                    )}
                </div>
                {/* Mobile: icon-only maps */}
                <IconButton
                    variant="secondary"
                    label={t('open_route_in_maps', { ns: 'events' })}
                    className="size-11 @[40rem]:hidden"
                    {...mapsLinkProps}
                >
                    <MapIcon className="size-5" />
                </IconButton>
                {/* Mobile: icon-only create */}
                <IconButton
                    variant="primary"
                    label={t('page_header__new_event', { ns: 'events' })}
                    className="size-11 @[40rem]:hidden"
                    onClick={openCreateEvent}
                    disabled={!canCreateEvent}
                >
                    <PlusIcon className="size-5" />
                </IconButton>
                {/* View toggle + paging: own line on mobile until the swipeable day strip (RETAIN-1462) replaces it */}
                <div className="flex w-full items-center justify-between gap-cozy @[40rem]:w-auto">
                    <ToggleGroup
                        selected={viewMode}
                        onSelectedChange={(value) => {
                            if (value === 'day' || value === 'week') {
                                onViewModeChange(value);
                            }
                        }}
                    >
                        <InsetToggleButton optionValue="day">
                            <CalendarIcon />
                            {t('period__day', { ns: 'events' })}
                        </InsetToggleButton>
                        <InsetToggleButton optionValue="week">
                            <CalendarDaysIcon />
                            {t('period__week', { ns: 'events' })}
                        </InsetToggleButton>
                    </ToggleGroup>
                    <div className="flex items-center gap-tight">
                        <Button
                            variant="secondary"
                            className="hidden @[40rem]:inline-flex"
                            disabled={isCurrentPeriod}
                            onClick={goToToday}
                        >
                            {t('today', { ns: 'events' })}
                        </Button>
                        <IconButton
                            label={
                                viewMode === 'day'
                                    ? t('calendar_nav__previous_day', { ns: 'events' })
                                    : t('calendar_nav__previous_week', { ns: 'events' })
                            }
                            variant="secondary"
                            onClick={() => {
                                handleChange(subDays(date, step), 'previous');
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            label={
                                viewMode === 'day'
                                    ? t('calendar_nav__next_day', { ns: 'events' })
                                    : t('calendar_nav__next_week', { ns: 'events' })
                            }
                            variant="secondary"
                            onClick={() => {
                                handleChange(addDays(date, step), 'next');
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                </div>
                {/* Desktop actions */}
                <div className="ml-auto hidden items-center gap-default @[40rem]:flex">
                    <ToolbarButton>
                        <UserCircleIcon className="size-4" />
                        {t('foreign_calendar', { ns: 'events' })}
                    </ToolbarButton>
                    <ToolbarButton aria-label={t('open_route_in_maps', { ns: 'events' })} {...mapsLinkProps}>
                        <MapIcon className="size-4" />
                        {t('maps', { ns: 'events' })}
                        <ArrowUpRightIcon className="size-4" />
                    </ToolbarButton>
                    <Button variant="primary" onClick={openCreateEvent} disabled={!canCreateEvent}>
                        <PlusIcon className="size-4" />
                        {t('page_header__new_event', { ns: 'events' })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
